// src/pages/PaymentPortalPage.jsx
// Public tenant-facing payment portal — no authentication required.
//
// URL format: /pay/:propertyId/:tenantId?m={messageId}&c={channel}
// The messageId query param links this payment back to the specific
// SMS or autopilot message that brought the tenant here (attribution).
//
// Flow:
//  1. Load tenant data via Cloud Function (safe subset only)
//  2. Tenant enters payment amount
//  3. Submit → Cloud Function records payment + updates balance + logs attribution
//  4. Show success / receipt screen

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { formatCurrency } from '../utils/formatters';

// ── Spinner ───────────────────────────────────────────────────────────────────
function Spinner({ size = 5, color = 'white' }) {
  return (
    <div
      className={`w-${size} h-${size} border-2 border-${color} border-t-transparent rounded-full animate-spin`}
    />
  );
}

// ── Logo header (minimal Tenafi brand on a public page) ───────────────────────
function PortalHeader() {
  return (
    <div className="flex flex-col items-center pt-10 pb-6">
      {/* Wordmark — mirrors the app's brand color */}
      <div className="flex items-center gap-2 mb-1">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
          <span className="text-white font-black text-sm">T</span>
        </div>
        <span className="text-xl font-bold text-gray-900 tracking-tight">Tenafi</span>
      </div>
      <p className="text-xs text-gray-400">Secure Resident Payment Portal</p>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function PaymentPortalPage() {
  const { propertyId, tenantId } = useParams();
  const [searchParams] = useSearchParams();

  // Attribution params from the payment link
  const messageId = searchParams.get('m') || null;
  const channel   = searchParams.get('c') || 'sms';
  const token     = searchParams.get('t') || '';

  // Tenant info (fetched from Cloud Function)
  const [tenantInfo, setTenantInfo]   = useState(null);
  const [loadError, setLoadError]     = useState('');
  const [loading, setLoading]         = useState(true);

  // Payment form state
  const [amount, setAmount]           = useState('');
  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [receipt, setReceipt]         = useState(null); // { paymentId, newBalance, amount }

  // ── Load tenant info on mount ─────────────────────────────────────────────
  useEffect(() => {
    if (!propertyId || !tenantId || !token) {
      setLoadError('Invalid payment link. Please contact your property manager.');
      setLoading(false);
      return;
    }

    const getPageData = httpsCallable(functions, 'getPaymentPageData');
    getPageData({ propertyId, tenantId, token })
      .then((result) => {
        const d = result.data;
        setTenantInfo(d);
        // Pre-fill with full balance
        setAmount(d.currentBalance > 0 ? d.currentBalance.toFixed(2) : '');
        setLoading(false);
      })
      .catch((err) => {
        console.error('Portal load error:', err);
        setLoadError(
          err.code === 'not-found'
            ? 'This payment link has expired or is invalid. Please contact your property manager.'
            : 'Unable to load payment information. Please try again or contact your property manager.'
        );
        setLoading(false);
      });
  }, [propertyId, tenantId, token]);

  // ── Quick-fill helpers ────────────────────────────────────────────────────
  const fillFull    = () => setAmount((tenantInfo?.currentBalance || 0).toFixed(2));
  const fillHalf    = () => setAmount(((tenantInfo?.currentBalance || 0) / 2).toFixed(2));

  // Validate: positive number, not more than balance
  const parsedAmount   = parseFloat(amount) || 0;
  const balance        = tenantInfo?.currentBalance || 0;
  const isValidAmount  = parsedAmount > 0 && parsedAmount <= balance + 0.01;
  const isFullPayment  = parsedAmount >= balance - 0.01;

  // ── Submit payment ────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValidAmount || submitting) return;

    setSubmitting(true);
    setSubmitError('');

    try {
      const processPayment = httpsCallable(functions, 'processPortalPayment');
      const result = await processPayment({
        propertyId,
        tenantId,
        token,
        amount: parsedAmount,
        messageId,
        channel,
      });

      const { paymentId, newBalance } = result.data;
      setReceipt({ paymentId, newBalance, amount: parsedAmount });
    } catch (err) {
      console.error('Payment submission error:', err);
      setSubmitError(
        'Payment could not be submitted. Please try again or contact your property manager.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading state ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <PortalHeader />
        <div className="flex items-center gap-3 text-gray-500 mt-4">
          <Spinner size={5} color="blue-500" />
          <span className="text-sm">Loading payment details…</span>
        </div>
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────────
  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
        <PortalHeader />
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-red-100 p-8 text-center mt-4">
          <div className="text-4xl mb-3">⚠️</div>
          <h2 className="font-semibold text-gray-900 mb-2">Unable to Load</h2>
          <p className="text-sm text-gray-500">{loadError}</p>
        </div>
      </div>
    );
  }

  // ── Success / Receipt screen ──────────────────────────────────────────────
  if (receipt) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
        <PortalHeader />
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-green-100 p-8 text-center mt-4">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">✅</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Payment Received</h2>
          <p className="text-sm text-gray-500 mb-6">Thank you, {tenantInfo?.name}.</p>

          {/* Receipt card */}
          <div className="bg-gray-50 rounded-xl border border-gray-200 divide-y divide-gray-100 text-left mb-6">
            <div className="flex justify-between items-center px-4 py-3 text-sm">
              <span className="text-gray-500">Amount paid</span>
              <span className="font-semibold text-gray-900">{formatCurrency(receipt.amount)}</span>
            </div>
            <div className="flex justify-between items-center px-4 py-3 text-sm">
              <span className="text-gray-500">Remaining balance</span>
              <span className={`font-semibold ${receipt.newBalance === 0 ? 'text-green-600' : 'text-gray-900'}`}>
                {receipt.newBalance === 0 ? '✓ Paid in full' : formatCurrency(receipt.newBalance)}
              </span>
            </div>
            <div className="flex justify-between items-center px-4 py-3 text-sm">
              <span className="text-gray-500">Unit</span>
              <span className="text-gray-700">{tenantInfo?.unit}</span>
            </div>
            <div className="flex justify-between items-center px-4 py-3 text-sm">
              <span className="text-gray-500">Property</span>
              <span className="text-gray-700">{tenantInfo?.propertyName}</span>
            </div>
            <div className="flex justify-between items-center px-4 py-3 text-sm">
              <span className="text-gray-500">Confirmation #</span>
              <span className="text-gray-400 font-mono text-xs">{receipt.paymentId?.slice(0, 8).toUpperCase()}</span>
            </div>
          </div>

          <p className="text-xs text-gray-400">
            A record of this payment has been shared with your property manager.
            Please save this page as your receipt.
          </p>
        </div>
        <p className="text-xs text-gray-400 mt-6">Powered by Tenafi AI · Secure payments</p>
      </div>
    );
  }

  // ── Main payment form ─────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-8">
      <PortalHeader />

      <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mt-4">

        {/* Tenant + property header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-5 text-white">
          <p className="text-blue-100 text-xs font-semibold uppercase tracking-wide mb-0.5">
            {tenantInfo?.propertyName || 'Resident Portal'}
          </p>
          <h1 className="text-xl font-bold">{tenantInfo?.name}</h1>
          {tenantInfo?.unit && (
            <p className="text-blue-200 text-sm mt-0.5">Unit {tenantInfo.unit}</p>
          )}
        </div>

        <div className="p-6 space-y-5">

          {/* Balance display */}
          {balance > 0 ? (
            <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-xl px-5 py-4">
              <div>
                <p className="text-xs text-red-500 font-semibold uppercase tracking-wide">Outstanding Balance</p>
                <p className="text-2xl font-bold text-red-600 mt-0.5">{formatCurrency(balance)}</p>
              </div>
              <span className="text-3xl">🏠</span>
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-xl px-5 py-4 text-center">
              <p className="text-green-700 font-semibold">✓ Your account is current</p>
              <p className="text-green-600 text-sm mt-0.5">No balance due at this time.</p>
            </div>
          )}

          {/* Payment form — only show if there's a balance */}
          {balance > 0 && (
            <form onSubmit={handleSubmit} className="space-y-4">

              {/* Amount input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Payment Amount
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">$</span>
                  <input
                    type="number"
                    min="1"
                    max={balance.toFixed(2)}
                    step="0.01"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    placeholder={balance.toFixed(2)}
                    className="w-full border border-gray-300 rounded-xl pl-8 pr-4 py-3 text-lg font-semibold text-gray-900 focus:ring-2 focus:ring-blue-500 outline-none"
                    required
                  />
                </div>

                {/* Quick-fill buttons */}
                <div className="flex gap-2 mt-2">
                  <button type="button" onClick={fillFull}
                    className="flex-1 py-1.5 px-3 text-xs font-medium border border-gray-200 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors">
                    Full balance ({formatCurrency(balance)})
                  </button>
                  <button type="button" onClick={fillHalf}
                    className="flex-1 py-1.5 px-3 text-xs font-medium border border-gray-200 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors">
                    Half ({formatCurrency(balance / 2)})
                  </button>
                </div>
              </div>

              {/* Payment preview */}
              {parsedAmount > 0 && isValidAmount && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-blue-600">Payment</span>
                    <span className="font-semibold text-blue-700">{formatCurrency(parsedAmount)}</span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-blue-600">Remaining balance</span>
                    <span className={`font-semibold ${isFullPayment ? 'text-green-600' : 'text-blue-700'}`}>
                      {isFullPayment ? '✓ Paid in full' : formatCurrency(balance - parsedAmount)}
                    </span>
                  </div>
                </div>
              )}

              {/* Error */}
              {submitError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
                  {submitError}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={!isValidAmount || submitting}
                className="w-full py-3.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold text-base hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <><Spinner size={5} color="white" /> Processing…</>
                ) : (
                  `Submit Payment of ${parsedAmount > 0 ? formatCurrency(parsedAmount) : '…'}`
                )}
              </button>
            </form>
          )}

          {/* Trust indicators */}
          <div className="flex items-center justify-center gap-4 pt-2">
            <span className="text-xs text-gray-400 flex items-center gap-1">🔒 Secure</span>
            <span className="text-xs text-gray-400 flex items-center gap-1">📧 Receipt via property manager</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-6 text-center space-y-1">
        <p className="text-xs text-gray-400">Powered by Tenafi AI</p>
        <p className="text-xs text-gray-400">
          Questions? Contact your property manager directly.
        </p>
      </div>
    </div>
  );
}
